import * as Sentry from "@sentry/react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { PATHS, ROUTES } from "@/lib/config/routesConfig";

import { AdminGuard } from "./AdminGuard";
import { AuthGuard } from "./AuthGuard";
import { ErrorBoundary } from "./ErrorBoundary";
import { LegacyAgentChatRedirect } from "./LegacyAgentChatRedirect";
import { RootLayout } from "./RootLayout";
import { SuspenseWrapper } from "./SuspenseWrapper";
import { lazyWithRetry } from "./lazyWithRetry";
import NotFound from "./not-found";

import { AgentsLayout } from "../agents/AgentsLayout";
import { OverviewLayout, OverviewRedirect } from "../overview/page";

const AcceptOrganisationInvitationPage = lazyWithRetry(
  () => import("../accept-organisation-invitation/page")
);
const AgentEditPage = lazyWithRetry(
  () => import("../agents/[agentId]/settings/page")
);
const AiStudio = lazyWithRetry(() => import("../ai-studio/page"));
const AssistantChat = lazyWithRetry(
  () => import("../assistants/[assistantId]/chat/page")
);
const ChatLayout = lazyWithRetry(() =>
  import("../assistants/[assistantId]/chat/ChatLayout").then((m) => ({
    default: m.ChatLayout,
  }))
);
const OrganisationPage = lazyWithRetry(() => import("../organisation/page"));
const Search = lazyWithRetry(() => import("../home/page"));
const StudioLayout = lazyWithRetry(() =>
  import("../studio/StudioLayout").then((m) => ({ default: m.StudioLayout }))
);
const BrainsManagement = lazyWithRetry(
  () => import("../studio/[brainId]/page")
);
const Studio = lazyWithRetry(() => import("../studio/page"));
const UserLayout = lazyWithRetry(() =>
  import("../user/page").then((m) => ({
    default: m.UserLayout,
  }))
);
const OverviewTab = lazyWithRetry(() =>
  import("../user/components/OverviewTab/OverviewTab").then((m) => ({
    default: m.OverviewTab,
  }))
);
const ToolConfigsTab = lazyWithRetry(() =>
  import("../user/components/ToolConfigsTab/ToolConfigsTab").then((m) => ({
    default: m.ToolConfigsTab,
  }))
);
const AuditLogsTab = lazyWithRetry(() =>
  import("../user/components/AuditLogsTab/AuditLogsTab").then((m) => ({
    default: m.AuditLogsTab,
  }))
);
const AgentToolsLogsTab = lazyWithRetry(() =>
  import("../user/components/AgentToolsLogsTab/AgentToolsLogsTab").then(
    (m) => ({
      default: m.AgentToolsLogsTab,
    })
  )
);
const UsersTab = lazyWithRetry(() =>
  import("../user/components/UsersTab/UsersTab").then((m) => ({
    default: m.UsersTab,
  }))
);
const TableTab = lazyWithRetry(() =>
  import("../overview/components/TableTab/TableTab").then((m) => ({
    default: m.TableTab,
  }))
);
const AnalyticsTab = lazyWithRetry(() =>
  import("../overview/components/AnalyticsTab/AnalyticsTab").then((m) => ({
    default: m.AnalyticsTab,
  }))
);
const ForgotPassword = lazyWithRetry(
  () => import("../(auth)/forgot-password/page")
);
const Login = lazyWithRetry(() => import("../(auth)/login/page"));
const ResetPassword = lazyWithRetry(
  () => import("../(auth)/reset-password/page")
);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter;

const createRouter = sentryCreateBrowserRouter
  ? sentryCreateBrowserRouter(createBrowserRouter)
  : createBrowserRouter;

export const router = createRouter([
  {
    path: "/",
    element: (
      <ErrorBoundary>
        <RootLayout />
      </ErrorBoundary>
    ),
    errorElement: <NotFound />,
    children: [
      {
        path: "login",
        element: (
          <SuspenseWrapper>
            <Login />
          </SuspenseWrapper>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <SuspenseWrapper>
            <ForgotPassword />
          </SuspenseWrapper>
        ),
      },
      {
        path: "reset-password",
        element: (
          <SuspenseWrapper>
            <ResetPassword />
          </SuspenseWrapper>
        ),
      },
      {
        path: "accept-organisation-invitation",
        element: (
          <SuspenseWrapper>
            <AcceptOrganisationInvitationPage />
          </SuspenseWrapper>
        ),
      },
      // Protected routes
      {
        element: <AuthGuard />,
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <Search />
              </SuspenseWrapper>
            ),
          },
          {
            path: ROUTES.AI_STUDIO,
            children: [
              {
                index: true,
                element: (
                  <SuspenseWrapper>
                    <AiStudio />
                  </SuspenseWrapper>
                ),
              },
              {
                path: ROUTES.AGENTS,
                element: <Navigate to={PATHS.AI_STUDIO_AGENTS} replace />,
              },
              {
                path: ROUTES.ASSISTANTS,
                element: <Navigate to={PATHS.AI_STUDIO_ASSISTANTS} replace />,
              },
            ],
          },
          {
            path: "agents",
            element: <AgentsLayout />,
            children: [
              {
                index: true,
                element: <Navigate to={PATHS.AI_STUDIO_AGENTS} replace />,
              },
              {
                path: ":agentId/settings",
                element: <AdminGuard />,
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <AgentEditPage />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
              {
                path: ":agentId/chat/:chatId?",
                element: <LegacyAgentChatRedirect />,
              },
            ],
          },
          {
            path: ROUTES.ASSISTANTS,
            element: <AgentsLayout />,
            children: [
              {
                index: true,
                element: <Navigate to={PATHS.AI_STUDIO_ASSISTANTS} replace />,
              },
              {
                path: ":assistantId/chat",
                element: (
                  <SuspenseWrapper>
                    <ChatLayout />
                  </SuspenseWrapper>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <AssistantChat />
                      </SuspenseWrapper>
                    ),
                  },
                  {
                    path: ":chatId",
                    element: (
                      <SuspenseWrapper>
                        <AssistantChat />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "studio",
            element: (
              <SuspenseWrapper>
                <StudioLayout />
              </SuspenseWrapper>
            ),
            children: [
              {
                index: true,
                element: (
                  <SuspenseWrapper>
                    <Studio />
                  </SuspenseWrapper>
                ),
              },
              {
                path: ":brainId",
                element: (
                  <SuspenseWrapper>
                    <BrainsManagement />
                  </SuspenseWrapper>
                ),
              },
            ],
          },
          {
            path: "organisation",
            element: (
              <SuspenseWrapper>
                <OrganisationPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: ROUTES.ANALYTICS,
            element: <OverviewRedirect to={PATHS.ANALYTICS} />,
          },
          {
            path: PATHS.OVERVIEW,
            element: <OverviewLayout />,
            children: [
              {
                index: true,
                element: <OverviewRedirect to={PATHS.TABLE} />,
              },
              {
                path: ROUTES.TABLE,
                element: (
                  <SuspenseWrapper>
                    <TableTab />
                  </SuspenseWrapper>
                ),
              },
              {
                path: ROUTES.ANALYTICS,
                element: (
                  <SuspenseWrapper>
                    <AnalyticsTab />
                  </SuspenseWrapper>
                ),
              },
            ],
          },
          {
            path: "user",
            element: (
              <SuspenseWrapper>
                <UserLayout />
              </SuspenseWrapper>
            ),
            children: [
              {
                index: true,
                element: (
                  <SuspenseWrapper>
                    <OverviewTab />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "tool-configs",
                element: <AdminGuard />,
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <ToolConfigsTab />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
              {
                path: "audit-logs",
                element: <AdminGuard />,
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <AuditLogsTab />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
              {
                path: "agent-tools-logs",
                element: <AdminGuard />,
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <AgentToolsLogsTab />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
              {
                path: "user-management",
                element: <AdminGuard />,
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <UsersTab />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
